import {
  ProgramNode,
  Statement,
  Expression,
  ForLoopNode,
  WhileLoopNode,
  DoWhileLoopNode,
  BlockStatement,
  DeclarationStatement,
  AssignmentStatement,
  CompoundAssignmentStatement,
  ExpressionStatement,
  UpdateExpression,
  BinaryExpression,
  UnaryExpression,
  Identifier,
  Literal,
  Variable,
  LoopContext,
  ExecutionState,
  ExecutionStep,
  RuntimeError,
} from '../types'
import { ExecutionContextManager } from './ExecutionContext'
import { LoopStateManager } from './LoopState'

export interface ExecutionEngineOptions {
  maxSteps?: number
  onStep?: (step: ExecutionStep) => void
  onError?: (error: RuntimeError) => void
  onVariableChange?: (name: string, variable: Variable) => void
  onComplete?: () => void
  onLoopStackUpdate?: (loopStack: LoopContext[]) => void
}

export class ExecutionEngine {
  private ast: ProgramNode
  private state: ExecutionState
  private executionHistory: ExecutionStep[] = []
  private options: ExecutionEngineOptions
  private stepTimer: number | null = null
  private isExecuting: boolean = false
  private contextManager: ExecutionContextManager
  private loopStateManager: LoopStateManager

  constructor(ast: ProgramNode, options: ExecutionEngineOptions = {}) {
    this.ast = ast
    this.options = {
      maxSteps: 10000,
      ...options,
    }
    this.state = this.initializeState()
    this.contextManager = new ExecutionContextManager(ast.body)
    this.loopStateManager = new LoopStateManager()
  }

  private initializeState(): ExecutionState {
    return {
      currentLine: 1,
      variables: new Map(),
      loopStack: [],
      isRunning: false,
      stepCount: 0,
    }
  }

  // 実行制御メソッド
  public run(speed: number = 100): void {
    if (this.isExecuting) return
    
    this.state.isRunning = true
    this.isExecuting = true
    
    const executeStep = () => {
      if (!this.state.isRunning) {
        return
      }
      
      if (this.state.stepCount >= this.options.maxSteps!) {
        this.pause()
        this.handleError({
          type: 'INFINITE_LOOP',
          message: '無限ループの可能性があります',
          context: this.state,
        })
        return
      }

      try {
        const hasMore = this.step()
        if (!hasMore) {
          this.pause()
          // 実行完了を通知
          if (this.options.onComplete) {
            this.options.onComplete()
          }
          return
        }
        
        this.stepTimer = setTimeout(executeStep, speed) as unknown as number
      } catch (error) {
        this.pause()
        this.handleError({
          type: 'RUNTIME_ERROR',
          message: error instanceof Error ? error.message : '実行時エラー',
          context: this.state,
        })
      }
    }
    
    executeStep()
  }

  public pause(): void {
    this.state.isRunning = false
    this.isExecuting = false
    if (this.stepTimer !== null) {
      clearTimeout(this.stepTimer)
      this.stepTimer = null
    }
  }

  public reset(): void {
    this.pause()
    this.state = this.initializeState()
    this.executionHistory = []
    this.contextManager = new ExecutionContextManager(this.ast.body)
    this.loopStateManager.clear()
  }

  public step(): boolean {
    if (this.state.stepCount >= this.options.maxSteps!) {
      return false
    }

    const beforeState = this.cloneState()
    
    // ループの終端処理をチェック
    const context = this.contextManager.currentContext
    if (context && context.loopInfo && context.statementIndex >= context.statements.length) {
      // console.log(`[DEBUG] ループイテレーション終了処理`)
      return this.handleLoopIteration(context)
    }
    
    // 実行するステートメントを見つける
    const statement = this.contextManager.currentStatement
    if (!statement) {
      return false
    }

    // console.log(`[DEBUG] ステートメント実行: 行${statement.line}, タイプ: ${statement.type}`)
    // if (statement.type === 'expression') {
    //   const expr = (statement as ExpressionStatement).expression
    //   console.log(`  [DEBUG] expressionの詳細: type=${expr.type}`)
    //   if (expr.type === 'assignment') {
    //     console.log(`    [DEBUG] assignment: ${(expr as any).identifier} = ...`)
    //   }
    // }
    
    // ステートメントを実行
    const shouldAdvance = this.executeStatement(statement)
    
    const afterState = this.cloneState()
    
    // 実行ステップを記録
    const step: ExecutionStep = {
      lineNumber: this.state.currentLine,
      instruction: this.getInstructionText(statement),
      beforeState,
      afterState,
      timestamp: Date.now(),
    }
    
    this.executionHistory.push(step)
    this.state.stepCount++
    
    if (this.options.onStep) {
      this.options.onStep(step)
    }
    
    
    // 次のステートメントに進む
    if (shouldAdvance) {
      const hasMore = this.contextManager.advance()
      return hasMore
    }
    
    // advance()を呼ばないが、実行は継続する
    return true
  }

  // ステートメント実行メソッド
  private executeStatement(statement: Statement): boolean {
    switch (statement.type) {
      case 'declaration':
        this.executeDeclaration(statement as DeclarationStatement)
        return true
      case 'multiple-declaration' as any:
        // 複数の変数宣言を処理
        const multiDecl = statement as any
        for (const decl of multiDecl.declarations) {
          this.executeDeclaration(decl)
        }
        return true
      case 'assignment':
        this.executeAssignment(statement as AssignmentStatement)
        return true
      case 'compound':
        this.executeCompoundAssignment(statement as CompoundAssignmentStatement)
        return true
      case 'expression':
        this.executeExpressionStatement(statement as ExpressionStatement)
        return true
      case 'for':
        // forループの場合、実行後に新しいコンテキストが作成される
        this.executeForLoop(statement as ForLoopNode)
        // ループが完了している場合のみadvance()を呼ぶ
        if (this.loopStateManager.isCompleted(statement as ForLoopNode)) {
          return true
        }
        // それ以外（ループに入った場合）はadvance()を呼ばない
        return false
      case 'while':
        // whileループの場合、実行後に新しいコンテキストが作成される
        this.executeWhileLoop(statement as WhileLoopNode)
        // ループが完了している場合のみadvance()を呼ぶ
        if (this.loopStateManager.isCompleted(statement as WhileLoopNode)) {
          return true
        }
        // それ以外（ループに入った場合）はadvance()を呼ばない
        return false
      case 'do-while':
        // do-whileループの場合、実行後に新しいコンテキストが作成される
        this.executeDoWhileLoop(statement as DoWhileLoopNode)
        // ループが完了している場合のみadvance()を呼ぶ
        if (this.loopStateManager.isCompleted(statement as DoWhileLoopNode)) {
          return true
        }
        // それ以外（ループに入った場合）はadvance()を呼ばない
        return false
      case 'block':
        return this.executeBlock(statement as BlockStatement)
      default:
        return true
    }
  }

  private executeDeclaration(declaration: DeclarationStatement): void {
    const value = declaration.initialValue 
      ? this.evaluateExpression(declaration.initialValue)
      : this.getDefaultValue(declaration.dataType)
    
    // console.log(`[DEBUG Declaration] ${declaration.dataType} ${declaration.identifier} = ${value} (行${declaration.line})`)
    
    const variable: Variable = {
      name: declaration.identifier,
      type: declaration.dataType,
      value,
      scope: 'local',
      changed: true,
    }
    
    this.setVariable(declaration.identifier, variable)
    this.state.currentLine = declaration.line
  }

  private executeAssignment(assignment: AssignmentStatement): void {
    const value = this.evaluateExpression(assignment.value)
    const variable = this.getVariable(assignment.identifier)
    
    if (!variable) {
      throw new Error(`未定義の変数: ${assignment.identifier}`)
    }
    
    
    variable.value = value
    variable.changed = true
    this.setVariable(assignment.identifier, variable)
    this.state.currentLine = assignment.line
  }

  private executeCompoundAssignment(assignment: CompoundAssignmentStatement): void {
    const variable = this.getVariable(assignment.identifier)
    if (!variable) {
      throw new Error(`未定義の変数: ${assignment.identifier}`)
    }
    
    const rightValue = this.evaluateExpression(assignment.value)
    let newValue: number
    
    switch (assignment.operator) {
      case '+=':
        newValue = (variable.value as number) + (rightValue as number)
        break
      case '-=':
        newValue = (variable.value as number) - (rightValue as number)
        break
      case '*=':
        newValue = (variable.value as number) * (rightValue as number)
        break
      case '/=':
        newValue = (variable.value as number) / (rightValue as number)
        break
      case '%=':
        newValue = (variable.value as number) % (rightValue as number)
        break
    }
    
    variable.value = newValue
    variable.changed = true
    this.setVariable(assignment.identifier, variable)
    this.state.currentLine = assignment.line
  }

  private executeExpressionStatement(statement: ExpressionStatement): void {
    // console.log(`[DEBUG ExpressionStatement] 式のタイプ: ${statement.expression.type}, 行: ${statement.line}`)
    // console.log(`[DEBUG ExpressionStatement] 式の詳細:`, JSON.stringify(statement.expression, null, 2))
    
    // 式がassignmentの場合は特別処理
    if (statement.expression.type === 'assignment') {
      const assignment = statement.expression as any
      // console.log(`[DEBUG ExpressionStatement] assignment式を実行: ${assignment.identifier} = ...`)
      // AssignmentStatementとして実行
      this.executeAssignment({
        type: 'assignment',
        identifier: assignment.identifier,
        value: assignment.value,
        line: statement.line,
        column: statement.column,
      } as AssignmentStatement)
    } else {
      // console.log(`[DEBUG ExpressionStatement] その他の式: ${statement.expression.type}`)
      this.evaluateExpression(statement.expression)
    }
    this.state.currentLine = statement.line
  }

  // 式の評価
  private evaluateExpression(expression: Expression): number | string {
    switch (expression.type) {
      case 'literal':
        return (expression as Literal).value
      
      case 'identifier':
        const variable = this.getVariable((expression as Identifier).name)
        if (!variable) {
          throw new Error(`未定義の変数: ${(expression as Identifier).name}`)
        }
        return variable.value
      
      case 'binary':
        return this.evaluateBinaryExpression(expression as BinaryExpression)
      
      case 'unary':
        return this.evaluateUnaryExpression(expression as UnaryExpression)
      
      case 'update':
        return this.evaluateUpdateExpression(expression as UpdateExpression)
      
      case 'assignment':
        // assignmentは式として評価される場合、代入後の値を返す
        const assignment = expression as any
        this.executeAssignment(assignment)
        return this.getVariable(assignment.identifier)?.value || 0
        
      default:
        throw new Error(`未対応の式タイプ: ${expression.type}`)
    }
  }

  private evaluateBinaryExpression(expression: BinaryExpression): number | string {
    const left = this.evaluateExpression(expression.left)
    const right = this.evaluateExpression(expression.right)
    
    switch (expression.operator) {
      case '+': return (left as number) + (right as number)
      case '-': return (left as number) - (right as number)
      case '*': return (left as number) * (right as number)
      case '/': 
        if ((right as number) === 0) {
          throw new Error('Division by zero')
        }
        return (left as number) / (right as number)
      case '%': 
        if ((right as number) === 0) {
          throw new Error('Division by zero')
        }
        return (left as number) % (right as number)
      case '<': return (left as number) < (right as number) ? 1 : 0
      case '>': return (left as number) > (right as number) ? 1 : 0
      case '<=': return (left as number) <= (right as number) ? 1 : 0
      case '>=': return (left as number) >= (right as number) ? 1 : 0
      case '==': return left === right ? 1 : 0
      case '!=': return left !== right ? 1 : 0
      case '&&': return (left as number) && (right as number) ? 1 : 0
      case '||': return (left as number) || (right as number) ? 1 : 0
      default:
        throw new Error(`未対応の演算子: ${expression.operator}`)
    }
  }

  private evaluateUnaryExpression(expression: UnaryExpression): number {
    const operand = this.evaluateExpression(expression.operand)
    
    switch (expression.operator) {
      case '-': return -(operand as number)
      case '!': return (operand as number) ? 0 : 1
      default:
        throw new Error(`未対応の単項演算子: ${expression.operator}`)
    }
  }

  private evaluateUpdateExpression(expression: UpdateExpression): number {
    const variable = this.getVariable(expression.identifier)
    if (!variable) {
      throw new Error(`未定義の変数: ${expression.identifier}`)
    }
    
    const oldValue = variable.value as number
    const newValue = expression.operator === '++' ? oldValue + 1 : oldValue - 1
    
    variable.value = newValue
    variable.changed = true
    this.setVariable(expression.identifier, variable)
    
    return expression.prefix ? newValue : oldValue
  }

  // ループ実行
  private executeForLoop(forLoop: ForLoopNode): boolean {
    this.state.currentLine = forLoop.line
    
    // ループが完了済みかチェック
    if (this.loopStateManager.isCompleted(forLoop)) {
      // console.log(`[DEBUG ForLoop] ループは既に完了済み`)
      // ループが完了している場合は、次のステートメントに進むためtrueを返す必要がある
      // しかし、executeStatementでfalseに変換されるため、ここで特別な処理が必要
      return true
    }
    
    // 現在のコンテキストを確認（既にループ内にいるかチェック）
    const currentContext = this.contextManager.currentContext
    if (currentContext?.loopInfo?.loopNode === forLoop) {
      // 既にこのループのコンテキスト内にいる場合は何もしない
      return true
    }
    
    // 初回実行チェック
    if (!this.loopStateManager.isInitialized(forLoop)) {
      // 初期化を実行
      if (forLoop.init) {
        // console.log(`[DEBUG ForLoop] 初期化実行前`)
        this.executeStatement(forLoop.init)
        // console.log(`[DEBUG ForLoop] 初期化実行後: i=${this.getVariable('i')?.value}`)
      }
      this.loopStateManager.markInitialized(forLoop)
    }
    
    // 条件をチェック
    const conditionValue = this.evaluateExpression(forLoop.condition)
    
    // 初回のイテレーション（iter: 0）の場合、特別な処理
    const isFirstIteration = !this.state.loopStack.find(lc => 
      lc.condition === this.getExpressionText(forLoop.condition) && 
      lc.type === forLoop.type
    )
    
    if (isFirstIteration) {
      // 初回は現在の変数状態を開始時の変数として記録
      const currentVariables = Array.from(this.state.variables.values()).map(v => ({ ...v }))
      
      // console.log(`[ExecutionEngine] First iteration - recording iteration=1, i=${this.getVariable('i')?.value}, conditionResult=${!!conditionValue}`)
      
      const loopContext: LoopContext = {
        type: forLoop.type,
        level: this.state.loopStack.length,
        iteration: 1,  // 最初の実際のイテレーションは1として記録
        condition: this.getExpressionText(forLoop.condition),
        evaluatedCondition: this.getEvaluatedExpressionText(forLoop.condition),
        conditionResult: !!conditionValue,
        variables: currentVariables,
      }
      this.state.loopStack.push(loopContext)
      
      // UIに更新を通知
      if (this.options.onLoopStackUpdate) {
        this.options.onLoopStackUpdate(this.state.loopStack)
      }
    } else {
      // 2回目以降は通常の処理
      this.updateLoopStackWithCondition(forLoop, 1, !!conditionValue)  // 最初のイテレーションは1
    }
    
    if (!conditionValue) {
      // false評価をUIに反映させるため、コールバックを呼び出す
      if (this.options.onLoopStackUpdate) {
        this.options.onLoopStackUpdate(this.state.loopStack)
      }
      
      // 条件が偽の場合、ループを完了としてマーク
      this.loopStateManager.markCompleted(forLoop)
      // ループスタックをクリア
      this.popLoopStack()
      return true
    }
    
    // ループコンテキストを作成
    const loopInfo = {
      type: 'for' as const,
      initExecuted: true,
      conditionChecked: true,
      updateExecuted: false,
      iterationCount: 1,  // 最初のイテレーションは1から開始
      loopNode: forLoop,
    }
    
    // ループボディのステートメントを取得
    const bodyStatements = this.getLoopBodyStatements(forLoop.body)
    // console.log(`[DEBUG ForLoop] ループ開始: i=${this.getVariable('i')?.value}, iteration: 0`)
    // console.log(`[DEBUG ForLoop] ループボディのステートメント数: ${bodyStatements.length}`)
    // if (bodyStatements.length > 0) {
    //   console.log(`  [DEBUG] 最初のステートメント: 行${bodyStatements[0].line}, タイプ: ${bodyStatements[0].type}`)
    // }
    
    // ネストしたループの完了状態をリセット
    this.resetNestedLoopsInBody(bodyStatements)
    
    // 新しいコンテキストをプッシュ
    this.contextManager.pushContext(bodyStatements, loopInfo)
    
    // ループに入る
    // 注意: この時点でiterationCountは0、iは1
    return true
  }

  private executeWhileLoop(whileLoop: WhileLoopNode): boolean {
    this.state.currentLine = whileLoop.line
    
    // ループが完了済みかチェック
    if (this.loopStateManager.isCompleted(whileLoop)) {
      return true
    }
    
    // 現在のコンテキストを確認（既にループ内にいるかチェック）
    const currentContext = this.contextManager.currentContext
    if (currentContext?.loopInfo?.loopNode === whileLoop) {
      // 既にこのループのコンテキスト内にいる場合は何もしない
      return true
    }
    
    // 初回のマーク
    if (!this.loopStateManager.isInitialized(whileLoop)) {
      this.loopStateManager.markInitialized(whileLoop)
    }
    
    // 条件をチェック
    const conditionValue = this.evaluateExpression(whileLoop.condition)
    
    // 条件評価結果をループスタックに追加
    this.updateLoopStackWithCondition(whileLoop, 0, !!conditionValue)
    
    if (!conditionValue) {
      // false評価をUIに反映させるため、コールバックを呼び出す
      if (this.options.onLoopStackUpdate) {
        this.options.onLoopStackUpdate(this.state.loopStack)
      }
      
      // 条件が偽の場合、ループを完了としてマーク
      this.loopStateManager.markCompleted(whileLoop)
      // ループスタックをクリア
      this.popLoopStack()
      return true
    }
    
    // ループコンテキストを作成
    const loopInfo = {
      type: 'while' as const,
      iterationCount: 0,
      loopNode: whileLoop,
    }
    
    // ループボディのステートメントを取得
    const bodyStatements = this.getLoopBodyStatements(whileLoop.body)
    
    // 新しいコンテキストをプッシュ
    this.contextManager.pushContext(bodyStatements, loopInfo)
    return true
  }

  private executeDoWhileLoop(doWhileLoop: DoWhileLoopNode): boolean {
    this.state.currentLine = doWhileLoop.line
    
    // ループが完了済みかチェック
    if (this.loopStateManager.isCompleted(doWhileLoop)) {
      return true
    }
    
    // 現在のコンテキストを確認（既にループ内にいるかチェック）
    const currentContext = this.contextManager.currentContext
    if (currentContext?.loopInfo?.loopNode === doWhileLoop) {
      // 既にこのループのコンテキスト内にいる場合は何もしない
      return true
    }
    
    // 初回のマーク
    if (!this.loopStateManager.isInitialized(doWhileLoop)) {
      this.loopStateManager.markInitialized(doWhileLoop)
    }
    
    // do-whileは無条件で最初にループに入る
    const loopInfo = {
      type: 'do-while' as const,
      iterationCount: 0,
      loopNode: doWhileLoop,
    }
    
    // ループボディのステートメントを取得
    const bodyStatements = this.getLoopBodyStatements(doWhileLoop.body)
    
    // 新しいコンテキストをプッシュ
    this.contextManager.pushContext(bodyStatements, loopInfo)
    // do-whileは最初に条件をチェックしないので、イテレーション0を設定
    this.updateLoopStackWithCondition(doWhileLoop, 0, true)
    return true
  }

  private executeBlock(block: BlockStatement): boolean {
    this.state.currentLine = block.line
    
    // ブロックの新しいコンテキストを作成
    this.contextManager.pushContext(block.body)
    return true
  }
  
  // ループのイテレーション処理
  private handleLoopIteration(context: import('./ExecutionContext').ExecutionContext): boolean {
    if (!context.loopInfo) return false
    
    const loopInfo = context.loopInfo
    const loopNode = loopInfo.loopNode
    
    // ループ本体実行後の変数値を記録（すべてのループタイプで共通）
    const variablesAfterBody = Array.from(this.state.variables.values()).map(v => ({ ...v }))
    
    console.log('[ExecutionEngine] handleLoopIteration called:', {
      loopType: loopInfo.type,
      iteration: loopInfo.iterationCount,
      variablesAfter: variablesAfterBody.map(v => `${v.name}=${v.value}`).join(', ')
    })
    
    // 現在のイテレーションのループコンテキストを更新
    // ループスタックの後ろから検索（最新のループから）
    const currentLoopContext = [...this.state.loopStack].reverse().find(lc => 
      lc.type === loopNode.type &&
      lc.iteration === loopInfo.iterationCount &&
      lc.condition === this.getExpressionText(loopNode.condition)
    )
    
    console.log('[ExecutionEngine] Found current loop context:', !!currentLoopContext, {
      loopStackSize: this.state.loopStack.length,
      searchCriteria: {
        type: loopNode.type,
        iteration: loopInfo.iterationCount,
        condition: this.getExpressionText(loopNode.condition)
      }
    })
    
    if (currentLoopContext) {
      currentLoopContext.variablesAfter = variablesAfterBody
      console.log('[ExecutionEngine] Updated variablesAfter for loop context')
      // UIに更新を通知
      if (this.options.onLoopStackUpdate) {
        this.options.onLoopStackUpdate(this.state.loopStack)
      }
    }
    
    switch (loopInfo.type) {
      case 'for':
        
        // forループの更新式を実行
        const forLoop = loopNode as ForLoopNode
        
        // 更新式実行前の変数値を保存（次のイテレーションの開始時変数）
        const variablesBeforeUpdate = Array.from(this.state.variables.values()).map(v => ({ ...v }))
        
        if (forLoop.update) {
          this.executeStatement(forLoop.update)
        }
        
        // 条件をチェック
        const forCondition = this.evaluateExpression(forLoop.condition)
        
        // 条件評価結果をループスタックに追加（falseの場合も含む）
        // 重要: 次のイテレーションの開始時変数は更新式実行前の値を使用
        // console.log(`[ExecutionEngine] Recording next iteration condition check: iteration=${loopInfo.iterationCount + 1}, i=${this.getVariable('i')?.value}, result=${!!forCondition}`)
        this.updateLoopStackWithCondition(forLoop, loopInfo.iterationCount + 1, !!forCondition, variablesBeforeUpdate)
        
        if (!forCondition) {
          // false評価をUIに反映させるため、コールバックを呼び出す
          if (this.options.onLoopStackUpdate) {
            this.options.onLoopStackUpdate(this.state.loopStack)
          }
          
          // ループ終了
          // console.log(`[DEBUG] ループ終了: i=${this.getVariable('i')?.value}`)
          this.contextManager.popContext()
          this.popLoopStack()
          this.loopStateManager.markCompleted(forLoop)
          const hasMore = this.contextManager.currentStatement !== undefined
          // console.log(`[DEBUG] ループ終了後、次のステートメントがあるか: ${hasMore}`)
          return hasMore
        }
        
        // 次のイテレーションへ
        loopInfo.iterationCount++
        context.statementIndex = 0
        
        // ループボディ内のネストしたループの完了状態をリセット
        const bodyStatements = this.getLoopBodyStatements(forLoop.body)
        this.resetNestedLoopsInBody(bodyStatements)
        
        // 現在のイテレーション（更新式実行後）の開始を記録
        // 676行目は「次の」イテレーションの条件チェック結果を記録している
        // ここでは「現在の」イテレーションの開始を記録する必要がある
        const variablesForCurrentIteration = Array.from(this.state.variables.values()).map(v => ({ ...v }))
        console.log(`[ExecutionEngine] Recording current iteration start: iteration=${loopInfo.iterationCount}, i=${this.getVariable('i')?.value}`)
        this.updateLoopStackWithCondition(forLoop, loopInfo.iterationCount, true, variablesForCurrentIteration)
        return true
        
      case 'while':
        // while ループの条件をチェック
        const whileLoop = loopNode as WhileLoopNode
        const whileCondition = this.evaluateExpression(whileLoop.condition)
        
        // 条件評価結果をループスタックに追加（falseの場合も含む）
        this.updateLoopStackWithCondition(whileLoop, loopInfo.iterationCount + 1, !!whileCondition)
        
        if (!whileCondition) {
          // false評価をUIに反映させるため、コールバックを呼び出す
          if (this.options.onLoopStackUpdate) {
            this.options.onLoopStackUpdate(this.state.loopStack)
          }
          
          // ループ終了
          this.contextManager.popContext()
          this.popLoopStack()
          this.loopStateManager.markCompleted(whileLoop)
          return this.contextManager.currentStatement !== undefined
        }
        
        // 次のイテレーションへ
        loopInfo.iterationCount++
        context.statementIndex = 0
        const variablesForNextIterationWhile = Array.from(this.state.variables.values()).map(v => ({ ...v }))
        this.updateLoopStackWithCondition(whileLoop, loopInfo.iterationCount, true, variablesForNextIterationWhile)
        return true
        
      case 'do-while':
        // do-while ループの条件をチェック
        const doWhileLoop = loopNode as DoWhileLoopNode
        const doWhileCondition = this.evaluateExpression(doWhileLoop.condition)
        
        // 条件評価結果をループスタックに追加（falseの場合も含む）
        this.updateLoopStackWithCondition(doWhileLoop, loopInfo.iterationCount + 1, !!doWhileCondition)
        
        if (!doWhileCondition) {
          // false評価をUIに反映させるため、コールバックを呼び出す
          if (this.options.onLoopStackUpdate) {
            this.options.onLoopStackUpdate(this.state.loopStack)
          }
          
          // ループ終了
          this.contextManager.popContext()
          this.popLoopStack()
          this.loopStateManager.markCompleted(doWhileLoop)
          return this.contextManager.currentStatement !== undefined
        }
        
        // 次のイテレーションへ
        loopInfo.iterationCount++
        context.statementIndex = 0
        const variablesForNextIterationDo = Array.from(this.state.variables.values()).map(v => ({ ...v }))
        this.updateLoopStackWithCondition(doWhileLoop, loopInfo.iterationCount, true, variablesForNextIterationDo)
        return true
    }
    
    return false
  }
  
  // ループスタックの更新（条件結果を含む）
  private updateLoopStackWithCondition(loopNode: ForLoopNode | WhileLoopNode | DoWhileLoopNode, iteration: number, conditionResult: boolean, variables?: Variable[]): void {
    // このループノードに対応する既存のコンテキストを探す
    const existingIndex = this.state.loopStack.findIndex(lc => {
      // 同じループノードかどうかを判定（行番号で識別）
      return lc.condition === this.getExpressionText(loopNode.condition) && 
             lc.type === loopNode.type
    })
    
    const level = existingIndex >= 0 ? this.state.loopStack[existingIndex].level : this.state.loopStack.length
    
    const loopContext: LoopContext = {
      type: loopNode.type,
      level,
      iteration,
      condition: this.getExpressionText(loopNode.condition),
      evaluatedCondition: this.getEvaluatedExpressionText(loopNode.condition),
      conditionResult,
      variables: variables || Array.from(this.state.variables.values()),
    }
    
    // ループコンテキストを更新
    
    // 既存のループコンテキストを更新または追加
    if (existingIndex >= 0) {
      // 既存のループコンテキストを更新
      this.state.loopStack[existingIndex] = loopContext
    } else {
      // 新しいループコンテキストを追加
      this.state.loopStack.push(loopContext)
    }
  }
  
  
  private popLoopStack(): void {
    this.state.loopStack.pop()
  }

  // ヘルパーメソッド
  private getLoopBodyStatements(body: Statement[]): Statement[] {
    // ループ本体が単一のブロックステートメントの場合、その中身を取得
    if (body.length === 1 && body[0].type === 'block') {
      return (body[0] as BlockStatement).body
    }
    return body
  }
  
  /**
   * ボディ内のネストしたループの完了状態をリセット
   */
  private resetNestedLoopsInBody(statements: Statement[]): void {
    for (const stmt of statements) {
      if (stmt.type === 'for' || stmt.type === 'while' || stmt.type === 'do-while') {
        this.loopStateManager.resetCompleted(stmt)
      } else if (stmt.type === 'block') {
        // ブロック内も再帰的にチェック
        this.resetNestedLoopsInBody((stmt as BlockStatement).body)
      }
    }
  }
  
  private getVariable(name: string): Variable | undefined {
    const variable = this.state.variables.get(name)
    if (variable) {
      // 変更フラグをリセット
      variable.changed = false
    }
    return variable
  }

  private setVariable(name: string, variable: Variable): void {
    this.state.variables.set(name, variable)
    if (this.options.onVariableChange) {
      this.options.onVariableChange(name, variable)
    }
  }

  private getDefaultValue(type: 'int' | 'float' | 'char'): number | string {
    switch (type) {
      case 'int':
      case 'float':
        return 0
      case 'char':
        return '\0'
    }
  }

  private cloneState(): ExecutionState {
    return {
      currentLine: this.state.currentLine,
      variables: new Map(
        Array.from(this.state.variables.entries()).map(([key, value]) => [
          key,
          { ...value },
        ])
      ),
      loopStack: [...this.state.loopStack],
      isRunning: this.state.isRunning,
      stepCount: this.state.stepCount,
    }
  }

  private getInstructionText(statement: Statement): string {
    switch (statement.type) {
      case 'declaration':
        const decl = statement as DeclarationStatement
        return `${decl.dataType} ${decl.identifier}${decl.initialValue ? ' = ...' : ''}`
      case 'assignment':
        const assign = statement as AssignmentStatement
        return `${assign.identifier} = ...`
      case 'for':
        return 'for (...)'
      case 'while':
        return 'while (...)'
      case 'do-while':
        return 'do-while (...)'
      case 'expression':
        return 'expression'
      default:
        return statement.type
    }
  }
  
  private getExpressionText(expression: Expression): string {
    switch (expression.type) {
      case 'binary':
        const binary = expression as BinaryExpression
        return `${this.getExpressionText(binary.left)} ${binary.operator} ${this.getExpressionText(binary.right)}`
      case 'identifier':
        return (expression as Identifier).name
      case 'literal':
        return String((expression as Literal).value)
      default:
        return '...'
    }
  }
  
  private getEvaluatedExpressionText(expression: Expression): string {
    switch (expression.type) {
      case 'binary':
        const binary = expression as BinaryExpression
        return `${this.getEvaluatedExpressionText(binary.left)} ${binary.operator} ${this.getEvaluatedExpressionText(binary.right)}`
      case 'identifier':
        const name = (expression as Identifier).name
        const variable = this.getVariable(name)
        return variable ? String(variable.value) : name
      case 'literal':
        return String((expression as Literal).value)
      default:
        return '...'
    }
  }

  private handleError(error: RuntimeError): void {
    if (this.options.onError) {
      this.options.onError(error)
    }
  }

  // 公開メソッド
  public getState(): ExecutionState {
    return this.cloneState()
  }

  public getHistory(): ExecutionStep[] {
    return [...this.executionHistory]
  }

  public isRunning(): boolean {
    return this.state.isRunning
  }
  
  public getStepCount(): number {
    return this.state.stepCount
  }
}